import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { map, shareReplay, take, tap } from 'rxjs';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { DialogActionTypes } from 'src/app/notifications/dialogs';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { MediaQueryService } from 'src/app/services/media-query-service';
import { Category } from 'src/app/swagger-api';
import {
  NAME_SORT_OPTIONS,
  SortCategories,
  SortOptions,
} from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { ListController, ListMode, ListState, sortParams } from '../../../shared/list-controller/list-controller';
import {
  CATEGORY_ROUTES,
  categoryCreateTitle,
  categoryPageTitle,
  CategoryRoute,
  CategoryRoutePath,
  categoryTypeLabel,
  categoryUpdateDialogTitle,
} from '../category-helpers';
import { CategoriesStore } from '../category.service';

@Component({
  selector: 'manage-categories',
  templateUrl: './manage-categories.component.html',
  styleUrls: ['./manage-categories.component.scss'],
})
export class ManageCategoriesComponent {
  private router = inject(Router);
  private mediaQuery = inject(MediaQueryService);
  private activatedRoute = inject(ActivatedRoute);
  private categoriesStore = inject(CategoriesStore);
  private operationService = inject(OperationInfoService);
  private translateService = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  public readonly tabs: CategoryRoute[] = inject(CATEGORY_ROUTES);

  public readonly ListMode: typeof ListMode = ListMode;
  public readonly SORT_OPTIONS: SortOptions[] = NAME_SORT_OPTIONS;

  private readonly listController = new ListController<CategoryRoutePath, string>({
    filtering: '',
    queryKeyword: '',
    mode: ListMode.BROWSE,
    sorting: SortCategories.NAME_ASC,
    pagination: { currentPage: 1, totalItemsCount: 0, limit: 30 },
    context: this.activatedRoute.routeConfig.path as CategoryRoutePath,
  });

  public readonly isMobile$ = this.mediaQuery.match$;
  public readonly listState$ = this.listController.state$;

  public readonly pageTitle = categoryPageTitle(this.listController.snapshot.context);
  public readonly createButtonLabel = categoryCreateTitle(this.listController.snapshot.context);

  public readonly categoryTypeLabel: string = this.translate(
    categoryTypeLabel(this.listController.snapshot.context),
  ).toLowerCase();

  public readonly categories$ = this.categoriesStore.filteredCategories$.pipe(
    tap(({ total_count }) => this.listController.updateCount(total_count)),
    map(({ items }) => items),
    shareReplay(1),
  );

  public readonly sortParams$ = this.listController.state$.pipe(
    map((activeTab: ListState<CategoryRoutePath, string>) => sortParams[activeTab.sorting]),
  );

  protected expandedCategories: { [key: string]: boolean } = {};

  public constructor() {
    this.listController.params$
      .pipe(
        takeUntilDestroyed(),
        tap(({ pagination, sorting, queryKeyword }) => {
          this.categoriesStore.loadCategories(pagination, sorting, queryKeyword);
        }),
      )
      .subscribe();
  }

  public onPageChange(page: number): void {
    this.listController.patch({
      pagination: { ...this.listController.pagination, currentPage: page },
    });
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.listController.patch({
      sorting: sortOptions.sortCategory,
    });
  }

  public onTabClick($event: NgbNavChangeEvent<CategoryRoutePath>): void {
    promiseWithCatch(
      this.router.navigate([$event.nextId], {
        relativeTo: this.activatedRoute.parent,
      }),
    );
  }

  public onKeywordChanged(keyword: string): void {
    const { snapshot } = this.listController;
    const { pagination, queryKeyword } = this.listController.initialState;

    const state: ListState<CategoryRoutePath, string> = {
      ...snapshot,
      pagination,
      queryKeyword,
      mode: ListMode.SEARCH,
      sorting: SortCategories.NAME_ASC,
    };

    const _keyword: string = keyword.trim();

    if (snapshot.mode === ListMode.BROWSE && _keyword) {
      this.listController.patch({ ...state, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && _keyword) {
      this.listController.patch({ ...snapshot, queryKeyword: _keyword });
      return;
    }

    if (snapshot.mode === ListMode.SEARCH && !_keyword) {
      this.listController.patch({ ...state, mode: ListMode.BROWSE, queryKeyword: '' });
      return;
    }
  }

  public applyDefaultParams(): void {
    const { pagination, sorting, filtering, queryKeyword, mode } = this.listController.initialState;

    this.listController.patch({
      mode,
      pagination,
      sorting,
      filtering,
      queryKeyword,
    });
  }

  public toggleCategory(categoryId: string): void {
    this.expandedCategories[categoryId] = !this.expandedCategories[categoryId];
  }

  public openDialog(): void {
    const { context } = this.listController.snapshot;

    this.notificationService
      .createCategoryDialog({
        title: categoryCreateTitle(context),
        existedCategories: this.categoriesStore.existedCategories$,
        createParent: (title) => this.categoriesStore.createCategory(title),
        createChild: (title: string, categoryId: string) => this.categoriesStore.createSubCategory(title, categoryId),
      })
      .pipe(take(1))
      .subscribe((isCreated: boolean) => {
        if (!isCreated) return;

        this.onListUpdate();
        this.notificationService.showSuccessfulMessage(
          this.translate('SUCCESSFUL_NEW_ENTRY_ADDED_HINT', {
            category_type: this.categoryTypeLabel,
          }),
        );
      });
  }

  public updateCategory(category: Category): void {
    const { context } = this.listController.snapshot;

    this.notificationService
      .updateCategoryDialog({
        category,
        title: categoryUpdateDialogTitle(context),
        existedCategories: this.categoriesStore.existedCategories$,
        update: (categoryId: string, categoryTitle: string, parentId?: string) =>
          this.categoriesStore.updateCategory(categoryId, categoryTitle, parentId),
      })
      .pipe(take(1))
      .subscribe((isUpdated: boolean) => {
        if (!isUpdated) return;

        this.onListUpdate();
        this.notificationService.showSuccessfulMessage(
          this.translate('SUCCESSFUL_NEW_ENTRY_UPDATED_HINT', {
            category_type: this.categoryTypeLabel,
          }),
        );
      });
  }

  public removeCategory(category: Category): void {
    const notificationDescription = category.parent_id
      ? 'CONFIRM_SUBCATEGORY_REMOVAL_DESCRIPTION'
      : 'CONFIRM_CATEGORY_REMOVAL_DESCRIPTION';

    this.operationService.setupOperation(
      {
        confirmationDialog: {
          title: this.translate('CONFIRM_CATEGORY_REMOVAL_TITLE', { category_title: category.title }),
          description: this.translate(notificationDescription, { category_title: category.title }),
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CANCEL_HINT', 'btn-secondary'),
            this.modalAttributesService.createDialogButton(
              'BTN_REMOVE_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },

        successfulMessage: this.translate('SUCCESSFUL_DELETE_ENTRY_HINT', {
          category_type: this.categoryTypeLabel,
          category_name: category.title,
        }),
      },
      () => {
        this.categoriesStore
          .removeCategory(category.entity_id)
          .pipe(take(1))
          .subscribe(() => {
            this.onListUpdate();
          });
      },
    );
  }

  private onListUpdate(): void {
    this.categoriesStore.refresh();

    const { pagination } = this.listController;

    if (pagination.itemsCount === 1 && pagination.currentPage > 1) {
      const isPageWithLastItem: boolean = // total - offset = 1
        pagination.totalItemsCount - pagination.limit * (pagination.currentPage - 1) === 1;

      if (pagination.currentPage !== 1 && isPageWithLastItem) {
        this.listController.updatePage(pagination.currentPage - 1);
      }
    }

    this.listController.triggerParams();
  }

  private translate(key: string | Array<string>, interpolateParams?: object): string {
    return this.translateService.instant(key, interpolateParams) as string;
  }
}
